import { HttpErrorResponse, HttpInterceptorFn, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import Swal from 'sweetalert2';

import { ToastInput, ToastService } from '../services/toast.service';
import { SKIP_ERROR_TOAST } from './http-context';

/**
 * Safety net for API failures nobody else explains. It never replaces a caller's own error
 * handling — the error is *always* rethrown, so every existing `error:` callback, `catchError`
 * and page-level message keeps working exactly as before.
 *
 * <p>It speaks up for only two cases:
 * <ul>
 *   <li><b>status 0</b> — the request never reached the server (offline, CORS, backend down).
 *       No page can explain that, and without a toast the screen just sits there empty.</li>
 *   <li><b>5xx</b> — the server broke. Also not something a page can phrase usefully.</li>
 * </ul>
 *
 * <p>And it stays deliberately silent for:
 * <ul>
 *   <li><b>401</b> — {@link authInterceptor} owns session expiry and already shows the
 *       "เซสชันหมดอายุ" dialog; a toast here would double up on it.</li>
 *   <li><b>every other 4xx</b> (400/403/404/409…) — these are business outcomes the pages
 *       report with their own Thai copy. A missing optional draft legitimately 404s, and a
 *       citizen hitting a staff-only read legitimately 403s; neither is an incident.</li>
 * </ul>
 *
 * <p><b>Reads fade, writes stay.</b> The copy and the lifetime both come off `req.method`. A failed
 * GET cost the user nothing — the data simply did not arrive, they can try again, and a notice that
 * lingers past the next page is just clutter. A failed POST/PUT/PATCH/DELETE is different in kind:
 * someone may have spent ten minutes on a form, and a toast that evaporates after six seconds can
 * let them close the tab believing the work was saved. So write failures say plainly that nothing
 * was saved and stay up until acknowledged.
 *
 * <p><b>Deferred by one macrotask, then it may stand down. Please do not delete either half —
 * neither is the old SweetAlert2 workaround.</b> They were removed once on the theory that owning
 * the toast stack made both obsolete; only the *reason* went obsolete, and the pair had to come
 * back. What each is for now:
 *
 * <ol>
 *   <li><b>The `setTimeout`</b> exists because of ordering, not rendering. An interceptor's
 *       `catchError` always runs <i>before</i> the subscriber's `error:` callback, so a toast shown
 *       synchronously here is decided while the page has not yet had the chance to speak. A task
 *       boundary runs after the microtask queue drains, which covers both shapes this app uses: a
 *       synchronous `error:` handler, and an `await`ed call whose `catch` block fires a dialog.
 *       <i>Not</i> the old reason: Swal's single global instance used to <b>destroy</b> a toast
 *       fired before the page's own `Swal.fire`. {@link ToastService} owns its own stack now, so
 *       nothing can destroy a toast any more — toasts stack with each other and render above every
 *       dialog. That win is kept; this deferral does not touch it.</li>
 *   <li><b>The stand-down</b> exists to avoid <b>duplicating</b> a report the page already made.
 *       Nine call sites in this SPA answer a failed request with their own
 *       `Swal.fire({ icon: 'error', … })` carrying specific copy, and none of them passes
 *       `skipErrorToast()`. Without the check, one 5xx on a save produces two notices: the page's
 *       precise modal, plus a generic toast that never self-dismisses and follows the user across
 *       routes. The user has already been told, in better words. <i>Not</i> the old reason: the old
 *       guard stood down because firing would have wiped the dialog off the screen.</li>
 * </ol>
 *
 * <p>The stand-down happens strictly <i>before</i> a toast is created. Nothing here ever removes or
 * suppresses a toast that is already on screen.
 *
 * <p><b>The signal: which dialog, not whether one is open — and not which icon.</b> The obvious
 * check is `Swal.isVisible()`, and it is too blunt — it is equally true of the "ยืนยันการยื่นคำขอ"
 * confirm, the "เซสชันหมดอายุ" modal and the 1.6-second "บันทึกสำเร็จ" success flash, so a
 * background save failing at that moment would be swallowed and the user would never see
 * "ข้อมูลที่คุณกรอกยังไม่ถูกบันทึก". SweetAlert2 rebuilds its container and popup element from
 * scratch on every `fire()`, so the popup's identity is a per-dialog fingerprint: capture it when
 * the request fails, compare at the boundary, and the question becomes the narrow one — *did a
 * dialog appear in response to this failure?* A dialog already open at failure time is the same
 * element at the boundary and does not suppress; a dialog that closed in between leaves no popup
 * at all and does not suppress either — that `null` check is load-bearing, not defensive, because
 * without it "no dialog now" reads as "a different dialog now". Both are what make this reachable
 * without touching a single call site.
 *
 * <p>It is deliberately not keyed on the error icon. The sibling permit SPA reports a partly-failed
 * bulk action with `icon: signed > 0 ? 'warning' : 'error'`, and an icon-class check would double
 * up on the warning half; this SPA has no such handler yet, and the identity signal means adding
 * one will not silently start double-reporting. Nor on `Swal.isVisible()`, which measures layout
 * boxes jsdom does not have — a guard built on it can only be tested by stubbing the guard.
 * `getPopup()` reports truthfully everywhere, and both SPAs now key on the same thing.
 *
 * <p><b>What that still cannot tell apart</b>, honestly: a dialog the page opens in the very tick
 * of the failure for an unrelated reason is indistinguishable from a report of it, and suppresses
 * the toast. Conversely a page that reports *later* than one task (behind a second `await`, or a
 * `setTimeout`) is not seen, and the user gets both reports. Erring towards the double report is
 * the safer direction for a failed write, and the residual suppression needs a dialog to have
 * appeared in that same tick — which, in practice, is the page reporting it.
 *
 * <p>The portal serves anonymous visitors on public routes (address lookup and master-data fire
 * before login), so this interceptor deliberately depends on nothing session-shaped — no
 * `AuthService`, no injected state beyond the toast stack itself. It reacts to the transport
 * outcome only, which is equally correct with or without a logged-in user.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.startsWith('/api') || req.context.get(SKIP_ERROR_TOAST)) {
    return next(req);
  }

  // Interceptor functions run in an injection context, so this is safe here.
  const toasts = inject(ToastService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && shouldToast(error.status)) {
        // Decided one macrotask later, once the page has had its turn — see the note above.
        reportUnlessThePageBeatUsToIt(toasts, copyFor(req.method, error.status));
      }

      // Always rethrow: this interceptor reports, it never handles.
      return throwError(() => error);
    }),
  );
};

/**
 * Raises `toast` one macrotask from now — unless, by then, a dialog is on screen that was not the
 * one there when the request failed. See the note above for why identity, not visibility or icon.
 */
function reportUnlessThePageBeatUsToIt(toasts: ToastService, toast: ToastInput): void {
  const dialogAtFailure = Swal.getPopup();

  setTimeout(() => {
    const dialogNow = Swal.getPopup();
    const pageReportedIt = dialogNow !== null && dialogNow !== dialogAtFailure;
    if (!pageReportedIt) {
      toasts.show(toast);
    }
  });
}

function shouldToast(status: number): boolean {
  if (status === HttpStatusCode.Unauthorized) {
    return false;
  }

  return status === 0 || status >= HttpStatusCode.InternalServerError;
}

/** Methods that only ever read. Everything else is treated as a write — see the note above. */
const READ_METHODS: ReadonlySet<string> = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Copy for a read that failed: nothing was lost, so the notice fades on its own. */
const READ_SERVER_ERROR: ToastInput = {
  title: 'เกิดข้อผิดพลาดของระบบ',
  text: 'โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
  persistent: false,
};

const READ_NETWORK_ERROR: ToastInput = {
  title: 'เชื่อมต่อระบบไม่ได้',
  text: 'กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ตแล้วลองใหม่อีกครั้ง',
  persistent: false,
};

/** Copy for a write that failed: it must say the data is gone, and it must not disappear. */
const WRITE_SERVER_ERROR: ToastInput = {
  title: 'บันทึกข้อมูลไม่สำเร็จ',
  text: 'ระบบขัดข้อง ข้อมูลที่คุณกรอกยังไม่ถูกบันทึก กรุณาลองใหม่อีกครั้ง',
  persistent: true,
};

const WRITE_NETWORK_ERROR: ToastInput = {
  title: 'บันทึกข้อมูลไม่สำเร็จ',
  text: 'เชื่อมต่อระบบไม่ได้ ข้อมูลที่คุณกรอกยังไม่ถูกบันทึก กรุณาตรวจสอบการเชื่อมต่อแล้วลองใหม่',
  persistent: true,
};

function copyFor(method: string, status: number): ToastInput {
  const network = status === 0;

  if (READ_METHODS.has(method.toUpperCase())) {
    return network ? READ_NETWORK_ERROR : READ_SERVER_ERROR;
  }

  return network ? WRITE_NETWORK_ERROR : WRITE_SERVER_ERROR;
}
